// テキスト配置時の文字サイズの選好を localStorage に保存する。
// プリセット（最小・極小・小・中・大・特大・最大）と「px 指定」の任意値を扱えるよう、
// 最終的には数値 px で保持する。

const KEY = "refinement-canvas:text:font-size";

export type TextSizeKind =
  | "smallest"
  | "tiny"
  | "small"
  | "medium"
  | "large"
  | "huge"
  | "largest"
  | "custom";

/**
 * プリセットの px 値。小は既存の既定サイズと一致させる（変更点を最小化するため）。
 * 両端（smallest / largest）は「px 指定」で入力できる下限・上限とちょうど同じ値にする。
 * ここでは定数を参照せず数値を直接書き、一致は単体テストで固定する
 * （Record の初期化順と定数の宣言順に依存させないため）。
 */
export const TEXT_SIZE_PRESET_PX: Record<Exclude<TextSizeKind, "custom">, number> = {
  smallest: 4,
  tiny: 8,
  small: 16,
  medium: 24,
  large: 40,
  huge: 72,
  largest: 128,
};

/**
 * 「px 指定」で利用者が直接入力できる範囲。極端な値で UI が壊れないよう上下限を設ける。
 * 下限の 4px は、拡大率 400%（上限は 16 倍）で「小」（16px）と同じ見え方になる値。
 * これより小さいと等倍で字形が潰れ、図形がそこにあること自体に気づけなくなる。
 */
export const TEXT_SIZE_MIN_PX = 4;
export const TEXT_SIZE_MAX_PX = 128;

/** 保存値が無いとき・壊れているときに使う既定サイズ。 */
export const DEFAULT_TEXT_FONT_SIZE: number = TEXT_SIZE_PRESET_PX.small;

/** 数値を有効な px 範囲に丸める。 */
export function clampTextFontSize(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_TEXT_FONT_SIZE;
  return Math.min(TEXT_SIZE_MAX_PX, Math.max(TEXT_SIZE_MIN_PX, Math.round(value)));
}

/** 与えられた px がいずれかのプリセットと一致すれば該当 kind を、そうでなければ "custom" を返す。 */
export function resolveTextSizeKind(px: number): TextSizeKind {
  if (px === TEXT_SIZE_PRESET_PX.smallest) return "smallest";
  if (px === TEXT_SIZE_PRESET_PX.tiny) return "tiny";
  if (px === TEXT_SIZE_PRESET_PX.small) return "small";
  if (px === TEXT_SIZE_PRESET_PX.medium) return "medium";
  if (px === TEXT_SIZE_PRESET_PX.large) return "large";
  if (px === TEXT_SIZE_PRESET_PX.huge) return "huge";
  if (px === TEXT_SIZE_PRESET_PX.largest) return "largest";
  return "custom";
}

export function loadTextFontSize(): number {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) return DEFAULT_TEXT_FONT_SIZE;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return DEFAULT_TEXT_FONT_SIZE;
    return clampTextFontSize(parsed);
  } catch {
    return DEFAULT_TEXT_FONT_SIZE;
  }
}

/**
 * 文字サイズを保存し、保存できたかどうかを返す。
 * 呼び出し側（canvas-store の setter）は false のとき楽観更新をロールバックする。
 */
export function saveTextFontSize(px: number): boolean {
  try {
    window.localStorage.setItem(KEY, String(clampTextFontSize(px)));
    return true;
  } catch {
    // プライベートブラウジング・容量超過など、保存できない環境では false を返す。
    return false;
  }
}
